/* Utils */
import {matchUrlInRouteConfigs} from '../../core/route/routeUtils';
import {checkJetDeliveryLabelShown, generateProductStatus, generateVariantStatus} from '../product/productCommonUtils';
import {mapMap} from '../../common/collection/collectionUtils';
import {generateCategoryHierarchyForCategories, generateCategoryHierarchyForDavinci} from '../category/categoryUtils';
import {generateHex2a, generateSplitString} from '../search/searchUtils';
import {getBrowserCookie} from '../../../client/common/persistence/browserCookieUtils';

/* Constants */
import {DAVINCI_PAGES_BY_LOCATION_DICT, DAVINCI_PAGES_BY_ROUTES_DICT} from './davinci/davinciConstants';
import {HBUS_READY_EVENT} from './hbus/hbusConstants';
import {FACET_IDS} from '../../views/filter/facet/facetConstants';
import {LISTING_PRODUCTS_COUNT_FOR_ONE_PAGE} from '../../common/pagination/paginationConstants';
import {
  EXPRESS_DELIVERY_FACET_CUSTOM_ID,
  NEXT_DAY_DELIVERY_FACET_CUSTOM_ID,
} from '../filter/filterConstants';
import {JET_DELIVERY_TAG_LIST_NAME, JET_DELIVERY_TAG_NAME} from '../delivery/deliveryConstants';

// @todo move functions on related files(davinciUtils.js, googleAnalyticsUtils.js etc.)

function generateProductListEventRequest({
  category,
  products,
  algKey,
  deliveryLocation,
  pageNumber,
  pageType,
  pageValue,
  categoryHierarchy,
  searchPass,
  totalItem,
  resultMotificationId = '',
  appliedFilters,
  shippingType,
  isListingPage,
  utagData,
  queryParams,
  facetList,
  searchContext,
  isSearchPage,
  labels,
  scoreFunctionName,
  townToTownEAD
}) {

  let shipping_type = shippingType ? shippingType.slice(LISTING_PRODUCTS_COUNT_FOR_ONE_PAGE) : '';
  const bucketIdAndWasabiPageType = isSearchPage ? 'searchresults' : pageType;
  const tolkienAnalytics = generateBucketIdAndWasabi(bucketIdAndWasabiPageType);
  const experiments = window?.hepsiBus?.parsers?.getGlobals()?.experiments || [];

  return {
    products: products.map((product, index) => generateProductInfoForDavinci({
      product,
      position: index + 1,
      shipping_type: shipping_type ? shipping_type[index] : '',
      tagLabel: labels,
      withLabel: true,
      jetDeliveryLocation: deliveryLocation,
      townToTownEAD
    })),
    category_hierarchy: categoryHierarchy,
    category_status: isListingPage ? '' : generateCategoryStatusForDavinci(category),
    city: deliveryLocation?.city?.name || '',
    canonical_url: utagData ? utagData.canonical_url : '',
    page_number: pageNumber,
    page_value: pageValue,
    page_type: pageType,
    experiments,
    search_pass: searchPass && searchPass !== 'None' ? searchPass : '',
    total_item: totalItem,
    result_motification_id: resultMotificationId,
    filters: facetList,
    categories: isListingPage ? generateCategoryHierarchyForCategories(category, queryParams) : [],
    applied_filters: appliedFilters,
    search_context: searchContext,
    wasabi_experiment: tolkienAnalytics.wasabiExperiment,
    bucket_id: tolkienAnalytics.bucketId,
    search_function: scoreFunctionName,
    al_g: algKey,
  };
}

function generateCategoryStatusForDavinci(category) {
  if (!category) {
    return 'Inactive';
  } else if (category.isClosedCategory) {
    return 'Inactive';
  } else {
    return 'Active';
  }
}

function generateProductClickEventRequest({
  product,
  variant,
  category,
  categoryHierarchy,
  pageType,
  pageValue,
  position,
  location,
  shippingType,
  utagData,
  isListingPage,
  isSearchPage,
}) {
  const bucketIdAndWasabiPageType = isSearchPage ? 'searchresults' : pageType;
  const tolkienAnalytics = generateBucketIdAndWasabi(bucketIdAndWasabiPageType);

  let shipping_type = shippingType ? shippingType[position - 1] : '';
  return {
    product: generateProductInfoForDavinci({product, position, variant, shipping_type}),
    category_hierarchy: categoryHierarchy,
    page_value: pageValue,
    page_type: pageType,
    location,
    experiments: generateExperiments(),
    category_status: isListingPage ? '' : generateCategoryStatusForDavinci(category),
    canonical_url: utagData ? utagData.canonical_url : '',
    wasabi_experiment: tolkienAnalytics.wasabiExperiment,
    bucket_id: tolkienAnalytics.bucketId,
  };
}

function generateAddToCartClickEventRequest({
  product,
  variant,
  category,
  categoryHierarchy,
  location,
  pageType,
  pageValue,
  position,
  shippingType,
  utagData,
  isListingPage,
}) {
  let shipping_type;
  shipping_type = shippingType ? shippingType[position - 1] : '';
  const variantOrProduct = variant || product;

  return {
    products: [generateProductInfoForDavinci({product, position, variant, shipping_type, isUseListingId: true})],
    category_hierarchy: categoryHierarchy,
    page_type: pageType,
    page_value: pageValue,
    experiments: generateExperiments(),
    mainProduct: true,
    campaignId: '',
    location,
    merchant: variant ? variant.merchantName : product.merchantName,
    merchantId: variantOrProduct?.merchantId || '',
    category_status: isListingPage ? '' : generateCategoryStatusForDavinci(category),
    canonical_url: utagData ? utagData.canonical_url : '',
  };
}

function generatePreOrderClickEventRequest({
  product,
  variant,
  category,
  categoryHierarchy,
  location,
  pageType,
  pageValue,
  position,
  shippingType,
  utagData,
  isListingPage,
}) {
  let shipping_type;
  shipping_type = shippingType ? shippingType[position - 1] : '';
  const variantOrProduct = variant || product;
  return {
    products: [generateProductInfoForDavinci({product, position, variant, shipping_type, isUseListingId: true})],
    category_hierarchy: categoryHierarchy,
    page_type: pageType,
    page_value: pageValue,
    experiments: generateExperiments(),
    location,
    mainProduct: true,
    campaignId: '',
    merchant: variant ? variant.merchantName : product.merchantName,
    merchantId: variantOrProduct?.merchantId || '',
    category_status: isListingPage ? '' : generateCategoryStatusForDavinci(category),
    canonical_url: utagData ? utagData.canonical_url : '',
  };
}

function generateChangeVariantEventRequest({
  product,
  activeVariant,
  category,
  categoryHierarchy,
  cartCandidateVariantPropertySelectionMap,
  cartCandidateSelectedVariantPropertyIndexMap,
  location,
  pageValue,
  pageType,
  position,
  isMerchantChanged,
  isPriceChanged,
  shippingType,
  utagData,
  isListingPage,
}) {

  let shipping_type;
  shipping_type = shippingType && shippingType[position - 1];

  return {
    products: [generateProductInfoChangeVariant({
      product,
      position,
      activeVariant,
      shipping_type,
      isUseListingId: true,
    })],
    page_value: pageValue,
    page_type: pageType,
    category_hierarchy: categoryHierarchy,
    experiments: generateExperiments(),
    location,
    isMerchantChanged: isMerchantChanged,
    isPriceChanged: isPriceChanged,
    merchant: activeVariant ? {id: activeVariant.merchantId, name: activeVariant.merchantName} : null,
    variant: generateProductChangeForVariant({
      cartCandidateVariantPropertySelectionMap,
      cartCandidateSelectedVariantPropertyIndexMap,
    }),
    canonical_url: utagData ? utagData.canonical_url : '',
    category_status: isListingPage ? '' : generateCategoryStatusForDavinci(category),
    category: {id: category.categoryId, name: category.categoryName},
  };
}

function generateOpenVariantPopupEventRequest({
  product,
  activeVariant,
  category,
  categoryHierarchy,
  cartCandidateVariantPropertySelectionMap,
  cartCandidateSelectedVariantPropertyIndexMap,
  location,
  pageValue,
  pageType,
  position,
  shippingType,
  utagData,
  isListingPage,
}) {

  let shipping_type;
  shipping_type = shippingType && shippingType[position - 1];

  return {
    products: [generateProductInfoForDavinci({product, position, activeVariant, shipping_type, isUseListingId: true})],
    page_value: pageValue,
    page_type: pageType,
    category_hierarchy: categoryHierarchy,
    experiments: generateExperiments(),
    location,
    merchant: activeVariant ? {id: activeVariant.merchantId, name: activeVariant.merchantName} : null,
    variant: generateProductChangeForVariant({
      cartCandidateVariantPropertySelectionMap,
      cartCandidateSelectedVariantPropertyIndexMap,
    }),
    canonical_url: utagData ? utagData.canonical_url : '',
    category_status: isListingPage ? '' : generateCategoryStatusForDavinci(category),
    category: {id: category.categoryId, name: category.categoryName},
  };
}

function generateCloseVariantPopupEventRequest({
  product,
  activeVariant,
  category,
  categoryHierarchy,
  cartCandidateVariantPropertySelectionMap,
  cartCandidateSelectedVariantPropertyIndexMap,
  location,
  pageValue,
  pageType,
  position,
  shippingType,
  utagData,
  isListingPage,
}) {

  let shipping_type;
  shipping_type = shippingType && shippingType[position - 1];
  return {
    products: [generateProductInfoChangeVariant({
      product,
      activeVariant,
      position,
      shipping_type,
      isUseListingId: true,
    })],
    page_value: pageValue,
    page_type: pageType,
    category_hierarchy: categoryHierarchy,
    experiments: generateExperiments(),
    location,
    merchant: activeVariant ? {id: activeVariant.merchantId, name: activeVariant.merchantName} : null,
    variant: generateProductChangeForVariant({
      cartCandidateVariantPropertySelectionMap,
      cartCandidateSelectedVariantPropertyIndexMap,
    }),
    canonical_url: utagData ? utagData.canonical_url : '',
    category_status: isListingPage ? '' : generateCategoryStatusForDavinci(category),
    category: {id: category.categoryId, name: category.categoryName},
  };
}

function generateProductInfoForDavinci({
  product,
  variant,
  position = '',
  shipping_type = '',
  isUseListingId = false,
  withLabel,
  jetDeliveryLocation,
  townToTownEAD
}) {
  const variantOrProduct = variant || product;
  let davinciInfo = {
    adcampaignid: product.adInfo,
    shipping_type: shipping_type,
    sku: variantOrProduct.sku,
    product_status: variant ? generateVariantStatus(variant) : generateProductStatus(product),
    price: variantOrProduct.price[variantOrProduct.price.length - 1].value.toFixed(2),
    product_id: product.productId,
    tag_label: product?.tagLabels?.length > 0 ? product?.tagLabels?.[0]?.tagName : '',
    position,
    boosting_factors: product?.boostingFactors || [],
    campaignText: product?.campaignText || '',
    is_semantic: product?.isSemantic || false,
    customerReviewCount: product?.rating?.count || 0,
    customerReviewScore: product?.rating?.rate || 0,
    discountRate: product?.price?.[0]?.discountRate || 0,
    al_p: product?.al_p || '',
    labels: [
      ...(product.labels.winnerLabel || ''),
      ...(product.labels.affordabilityLabel || ''),
      ...(product.labels.imageLabel || ''),
      ...(product.labels.campaignLabels || '')
    ]
  };

  if (withLabel) {
    const productHasUserCityCode = product?.jetDeliveryCityCodes?.includes(jetDeliveryLocation?.city?.code);
    let tagList = [];

    if (product?.tagLabels?.length > 0) {
      tagList.push({
        id: product.tagLabels?.[0]?.tagName,
        displayname: product.tagLabels?.[0]?.tagDisplayName,
        type: 'tag',
      });
    }

    if (product.jetDelivery && productHasUserCityCode) {
      const showJetDeliveryLabel = checkJetDeliveryLabelShown({
        townToTownEAD,
        jetDeliveryCities: product?.jetDeliveryCities,
        location: jetDeliveryLocation,
        cityBasedCutOffEnabled: product?.cityBasedCutOffEnabled
      });

      if (showJetDeliveryLabel) {
        tagList.push({
          id: JET_DELIVERY_TAG_NAME,
          displayname: JET_DELIVERY_TAG_LIST_NAME,
          type: 'badge',
        });
      }
    }

    davinciInfo.labels = Array.isArray(davinciInfo.labels) ? [...davinciInfo.labels, ...tagList] : [...tagList];
  }


  if (isUseListingId) {
    davinciInfo.listingId = variantOrProduct.listingId;
  } else {
    davinciInfo.listing_id = variantOrProduct.listingId;
  }

  return davinciInfo;
}

function generateProductInfoChangeVariant({
  product,
  activeVariant,
  position = '',
  shipping_type = '',
  isUseListingId = false,
}) {
  const variantOrProduct = activeVariant || product;
  let davinciInfo = {
    sku: activeVariant ? activeVariant.sku : null,
    shipping_type: activeVariant ? shipping_type : null,
    product_status: activeVariant ? generateVariantStatus(activeVariant) : null,
    price: activeVariant ? activeVariant.price[activeVariant.price.length - 1].value.toFixed(2) : null,
    product_id: product.productId,
    tag_label: product?.tagLabels?.length > 0 ? product?.tagLabels?.[0]?.tagName : '',
    position,
  };

  if (isUseListingId) {
    davinciInfo.listingId = variantOrProduct.listingId;
  } else {
    davinciInfo.listing_id = variantOrProduct.listingId;
  }

  return davinciInfo;
}

function generateProductChangeForVariant({
  cartCandidateVariantPropertySelectionMap,
  cartCandidateSelectedVariantPropertyIndexMap,
}) {
  let variant = [];
  if (cartCandidateVariantPropertySelectionMap && cartCandidateSelectedVariantPropertyIndexMap) {
    mapMap(cartCandidateVariantPropertySelectionMap, (variantPropertyAvailabilitySet, variantPropertyName) =>
      variant.push(Object.assign(
        {value: cartCandidateVariantPropertySelectionMap.get(variantPropertyName).variantProperty.value},
        {name: cartCandidateVariantPropertySelectionMap.get(variantPropertyName).variantProperty.name},
        {position: cartCandidateSelectedVariantPropertyIndexMap.get(variantPropertyName) + 1}),
      ));
  }
  return variant;
}

function getDavinciPageByUrl(url, isSuggestion = false) {
  const routeMatchResult = matchUrlInRouteConfigs(url);
  let result;

  if (routeMatchResult) {
    result = isSuggestion ? DAVINCI_PAGES_BY_LOCATION_DICT[routeMatchResult.path] : DAVINCI_PAGES_BY_ROUTES_DICT[routeMatchResult.path];
  } else {
    result = null;
  }

  return result;
}

function generateExperiments() {
  let experiments = [];
  window.addEventListener(
    HBUS_READY_EVENT,
    function() {
      if (window.hepsiBus) {
        experiments = window.hepsiBus.parsers.getGlobals();
      }
    },
  );
  return experiments;
}

function generateFeedbackViewEventRequest({
  pageType,
  pageValue,
}) {

  return {
    page_value: pageValue,
    page_type: pageType,
  };
}

function generateFeedbackClickEventRequest({
  pageType,
  pageValue,
  actionType,
  clickedResponse,
  description,
}) {

  return {
    page_value: pageValue,
    page_type: pageType,
    action: actionType,
    clicked_response: clickedResponse,
    description: description,
  };
}

function generateFilterWidgetClickEventRequest({
  pageType,
  pageValue,
  widgetFilters,
}) {

  return {
    page_value: pageValue,
    page_type: pageType,
    widget_filters: widgetFilters,
  };
}

function generateFilterFacetClickEvent(facet, filter, category, pageValue, pageType, event = null, isCategoriesClick = false, location = null) {
  const result = {
    boost_type: filter?.boostType,
    filters: [
      {
        clicked_filter: isCategoriesClick
          ? {
            type: facet?.title,
            value: filter?.id,
            facet_mordor_key: filter?.mordorId || '',
            facet_mordor_value: filter?.id || '',
          }
          : {
            type: facet?.title,
            value: filter?.name,
            facet_mordor_key: facet?.mordorId || '',
            facet_mordor_value: filter?.mordorValue || '',
          },
        facet: {id: facet?.id, name: facet?.title},
        is_clicked: event && event.target ? Boolean(event.target.checked) : true,
        is_auto_applied: Boolean(filter?.isAutoApplied),
      }
    ],
    category_hierarchy: category ? generateCategoryHierarchyForDavinci(category) : [],
    page_value: pageValue,
    page_type: pageType,
    location: location || 'facet',
  };

  return result;
}

function generateCampaignFilterFacetClickEvent(facet, filter, category, pageValue, pageType, event = null, isCategoriesClick = false, location = 'facet') {
  const result = {
    is_clicked: event ? event : false,
    facet: {id: facet.id, name: facet.title},
    clicked_filter: isCategoriesClick
      ? {
        type: facet.title,
        value: filter.id,
        facet_mordor_key: filter.mordorId || '',
        facet_mordor_value: filter.id || '',
      }
      : {
        type: facet.title,
        value: filter.name,
        facet_mordor_key: facet.mordorId || '',
        facet_mordor_value: filter.mordorValue || '',
      },
    category_hierarchy: category ? generateCategoryHierarchyForDavinci(category) : [],
    page_value: pageValue,
    page_type: pageType,
    location: location,
  };

  return result;
}

function generateRemoveFiltersEvent({appliedFacetArray, category, pageValue, pageType, activeFacet, filteredCategory}) {
  const removedFilters = [];
  const removedFacet = [];

  if ((activeFacet && activeFacet.id === FACET_IDS.CATEGORY || !activeFacet) && filteredCategory) {

    removedFacet.push({
      id: 'category',
      name: 'Kategori',
    });

    if (filteredCategory.categoryName) {
      removedFilters.push({
        type: filteredCategory.idHierarchy,
        value: filteredCategory.categoryName,
        isAutoApplied: false,
      });
    }
  }

  if (activeFacet) {
    if (activeFacet.id !== FACET_IDS.CATEGORY) {
      removedFacet.push({
        id: activeFacet.id,
        name: activeFacet.title,
      });

      appliedFacetArray && appliedFacetArray.length > 0 && appliedFacetArray.map(facet => { // eslint-disable-line
        if (facet && facet.rawFacet && facet.rawFacet.items.length > 0 && facet.rawFacet.id === activeFacet.id) {
          facet.rawFacet.items.map(item => {  // eslint-disable-line
            removedFilters.push({
              type: facet.rawFacet.title,
              value: item.name,
              isAutoApplied: item.isAutoApplied,
            })
          })
        }
      });
    }
  } else {
    appliedFacetArray && appliedFacetArray.length > 0 && appliedFacetArray.map(facet => { // eslint-disable-line
      removedFacet.push({
        id: facet.id,
        name: facet.title,
      });

      if (facet && facet.rawFacet && facet.rawFacet.items.length > 0) {
        facet.rawFacet.items.map(item => {  // eslint-disable-line
          removedFilters.push({
            type: facet.rawFacet.title,
            value: item.value,
            isAutoApplied: item.isAutoApplied,
          })
        })
      }
    });
  }

  const result = {
    category_hierarchy: category ? generateCategoryHierarchyForDavinci(category) : [],
    removed_filters: removedFilters,
    facet: removedFacet,
    page_value: pageValue,
    page_type: pageType,
  };

  return result;
}

function generateFilterFieldForEvents(facetList, deliveryInfo) {
  let facetEventList = [];
  if (facetList && facetList.size > 0) {
    facetList.forEach((facet) => {
      let deliveryFacetStatus;
      const isNextDayDeliveryFilter = facet.customFacetId === NEXT_DAY_DELIVERY_FACET_CUSTOM_ID;
      const isExpressDeliveryFilter = facet.customFacetId === EXPRESS_DELIVERY_FACET_CUSTOM_ID;

      if (isNextDayDeliveryFilter) {
        deliveryFacetStatus = deliveryInfo?.nextDayDelivery?.selectedValues?.[0] && 'enabled' ||
          !deliveryInfo?.nextDayDelivery?.insideCutoff && 'cutoffDisabled' ||
          !deliveryInfo?.nextDayDelivery?.hasProduct && 'cityDisabled' ||
          'enabled';
      } else if (isExpressDeliveryFilter) {
        deliveryFacetStatus = deliveryInfo?.expressDelivery?.selectedValues?.[0] && 'enabled' ||
          !deliveryInfo?.expressDelivery?.insideCutoff && 'cutoffDisabled' ||
          !deliveryInfo?.expressDelivery?.hasProduct && 'cityDisabled' ||
          'enabled';
      }

      const facetStatus = facet.enabled !== false ? 'enabled' : 'disabled';

      facetEventList.push({
        hasMoreItem: facet.hasMoreItem,
        id: facet.id,
        isIndexable: facet.isIndexable,
        isSorted: facet.isSorted,
        title: facet.title,
        totalItemCount: facet.totalItemCount,
        facetStatus: deliveryFacetStatus ? deliveryFacetStatus : facetStatus,
        key: facet?.id,
      })
    });
  }
  return facetEventList;
}

function generateBucketIdAndWasabi(pageType) {
  const dgr_cookie = `dgr_${pageType}`;
  if (getBrowserCookie(dgr_cookie)) {
    const hexStringForTolkien = generateHex2a(getBrowserCookie(dgr_cookie));
    const splitedHex = generateSplitString(hexStringForTolkien, '|');
    return {
      wasabiExperiment: splitedHex[0],
      bucketId: splitedHex[1],
    }
  }
  return {
    wasabiExperiment: '',
    bucketId: '',
  }
}

function generateHomePageViewEvent({
  homePageComponents,
}) {
  let items = [];
  if (homePageComponents && homePageCompconents.length > 0) {
    homePageComponents.forEach((component, index) =>
      items.push({
        component_type: component.componentTypeId,
        component_name: component.componentTypeName,
        component_id: component.id,
        position: index + 1,
        placement_id: component.placementId ? component.placementId : '',
      }));
  }
  return {
    components: items,
  }
}

function generateSponsoredProductClick({
  pageType,
  pageValue,
  product,
  position,
}) {

  return {
    page_type: pageType,
    page_value: pageValue,
    products: [{
      adcampaignid: product.adInfo,
      listing_id: product.listingId,
      position: position,
      price: product.price[product.price.length - 1].value.toFixed(2),
      product_id: product.productId,
      product_status: generateProductStatus(product),
      shipping_type: '',
      sku: product.sku,
      trackingInfo: product?.trackingInfo ?? '',
    }],
  };
}

function generateProductListViewData({
  products,
  pageType,
  pageValue,
  pageNumber,
  algKey,
}) {
  const productsData = products?.map((item) => {
    return {
      listing_id: item?.listingId || '',
      position: item?.position,
      price: item?.price?.[0]?.formattedPrice || '',
      product_id: item?.productId,
      shipping_type: null,
      sku: item?.sku,
      customerReviewCount: item?.rating?.count || 0,
      customerReviewScore: item?.rating?.rate || 0,
      discountRate: item?.price?.[0]?.discountRate || 0,
      al_p: item?.al_p || '',
      labels: [
        ...(item.labels.winnerLabel || ''),
        ...(item.labels.affordabilityLabel || ''),
        ...(item.labels.imageLabel || ''),
        ...(item.labels.campaignLabels || '')
      ]
    };
  });

  return {
    page_type: pageType,
    page_value: pageValue,
    page_number: Number(pageNumber || 1),
    products: productsData || [],
    al_g: algKey || ''
  };
}

function generateSponsoredProductView({
  pageType,
  pageValue,
  products,
}) {
  const productsData = [];

  products.map(product => {  // eslint-disable-line
    productsData.push({
      adcampaignid: product.adInfo,
      listing_id: product.listingId,
      position: product.position,
      price: product.price[product.price.length - 1].value.toFixed(2),
      product_id: product.productId,
      product_status: generateProductStatus(product),
      shipping_type: '',
      sku: product.sku,
      trackingInfo: product?.trackingInfo ?? '',
      labels: [
        ...(product.labels.winnerLabel || ''),
        ...(product.labels.affordabilityLabel || ''),
        ...(product.labels.imageLabel || ''),
        ...(product.labels.campaignLabels || '')
      ]
    })
  })

  return {
    page_type: pageType,
    page_value: pageValue,
    products: productsData,
  };
}

function generateCampaignTagClickEvent({
  title,
  position,
  pageValue,
  pageType,
}) {
  return {
    page_type: pageType,
    page_value: pageValue,
    position: position + 1 || 1,
    text: title,
  };
}

function generateImageViewEvent(
  {
    pageValue,
    pageType,
    sku,
    productId,
    merchantName,
    merchantId,
    categoryName,
    categoryId,
    position,
    location,
    tagName,
    imageLabel,
    type
  }) {
  return {
    page_type: pageType,
    page_value: pageValue,
    position: position,
    sku: sku,
    productId: productId,
    merchantName: merchantName,
    merchantId: merchantId,
    categoryName: categoryName,
    categoryId: categoryId,
    location: location,
    tag_name: tagName,
    image_label: imageLabel,
    type
  };
}

function generateProductViewEvent(
  {
    clientType,
    experiments,
    canonical_url,
    category_hierarchy,
    merchant,
    category,
    products,
    review_rate,
    review_count,
    layout
  }) {
  return {
    clientType,
    experiments,
    canonical_url,
    category_hierarchy,
    merchant,
    category,
    products,
    review_rate,
    review_count,
    layout,
  };
}

export {
  generateProductListEventRequest,
  generateProductClickEventRequest,
  generateAddToCartClickEventRequest,
  getDavinciPageByUrl,
  generateExperiments,
  generatePreOrderClickEventRequest,
  generateChangeVariantEventRequest,
  generateOpenVariantPopupEventRequest,
  generateCloseVariantPopupEventRequest,
  generateFeedbackViewEventRequest,
  generateFeedbackClickEventRequest,
  generateFilterWidgetClickEventRequest,
  generateCategoryStatusForDavinci,
  generateProductInfoForDavinci,
  generateFilterFacetClickEvent,
  generateFilterFieldForEvents,
  generateBucketIdAndWasabi,
  generateHomePageViewEvent,
  generateRemoveFiltersEvent,
  generateProductListViewData,
  generateSponsoredProductClick,
  generateSponsoredProductView,
  generateCampaignTagClickEvent,
  generateCampaignFilterFacetClickEvent,
  generateImageViewEvent,
  generateProductViewEvent,
};
